<template>
  <div class="ly-search-comp">
    <el-select
      v-model="value"
      filterable
      :multiple="options.multiple"
      :collapse-tags="options['collapse-tags']"
      remote
      :clearable="options.clearable"
      :placeholder="options.placeholder"
      :remote-method="remoteMethod"
      @change="value => handleSelect(options.key, value)"
      @click.native="() => remoteMethod()"
      :loading="loading"
      v-if="options.type === 'select'"
      ref="select"
      :disabled="disabled"
    >
      <el-option
        v-for="item in nativeOptions"
        :key="item[options.value.toString()]"
        :label="item[options.label]"
        :value="item[options.value]"
        :disabled="typeof options.disabledKey ==='function' ?  options.disabledKey(item) : item[options.disabledKey]"
      >
        <span class="fl">{{ item[options.label] | longText }}</span>
        <div v-if="options.showOthers" class="fr m-l-5">
          <span v-for="(key, index) in options.showOthers" :key="index" v-show=" typeof key === 'function' ? key(item) : item[key]">
            {{typeof key === 'function' ?  key(item) : item[key] }}
            <span v-show="index < options.showOthers.length - 2">/</span>
          </span>
        </div>
      </el-option>
    </el-select>
    <el-autocomplete
      v-else
      v-model="value"
      :clearable="options.clearable"
      :fetch-suggestions="remoteMethodInput"
      :placeholder="options.placeholder"
      :value-format="options['value-format']"
      @select="item => handleSelect(options.key, item[options.value], item)"
      :value-key="options.label"
      :disabled="disabled"
    ></el-autocomplete>
  </div>
</template>

<script>
import { Autocomplete, Option } from 'element-ui';
import { base, classApi, contact } from '@/api';
import cPosts from '@/api/cPosts';
import cNews from '@/api/cNews';
import LySelect from '../LySelect';

export default {
  components: { ElSelect: LySelect, ElOption: Option, ElAutocomplete: Autocomplete },
  props: {
    options: {
      type: Object,
      required: true,
      default() {
        return {
          placeholder: '请输入关键词并选择',
          type: 'select', // input 还是select类型
          metholdKey: '', // 服务器请求接口key
          key: '', // 字段名
          label: '', // 显示字段
          value: '', // value字段
          staticResult: [] // 搜索结果默认保留值（比如：全部项目）
        };
      }
    },
    defaultData: {
      type: Object,
      default() {
        return {};
      }
    },
    paramsData: {
      type: Object,
      default() {
        return {};
      }
    },
    isCache: {
      type: Boolean,
      default() {
        return false;
      }
    }
  },
  data() {
    return {
      nativeOptions: [],
      value: '',
      nativeLabel: '',
      loading: false,
      nativeDef: {}, // 缓存上次默认值
      nativeDefPar: {}, // 缓存上次参数
      disabled: false,
      keys: Object.keys(this.paramsData)
    };
  },
  mounted() {
    this.init();
  },
  filters: {
    longText(value) {
      return value && value.length > 60 ? `${value.slice(0, 60)}...` : value;
    }
  },
  watch: {
    defaultData() {
      // value变化的时候才进行处理
      if (this.nativeDef.value !== this.defaultData.value) {
        this.init();
      }
    },
    paramsData() {
      this.check();
    }
  },
  methods: {
    clear() {
      // 重置表单（级联查询变化 or 主动重置）
      this.nativeOptions = [];
      this.nativeDef = {};
      this.nativeDefPar = {};
      if (this.value) this.$emit('input', '');
      this.value = '';
    },
    setValue(value) {
      this.value = value;
    },
    check() {
      // 级联查询时disable限制检查
      if (this.keys.length) {
        this.disabled = 'disabled' in this.options ? this.options.disabled : !this.keys.every(key => {
          if (Array.isArray(this.paramsData[key])) {
            return this.paramsData[key].length;
          }
          return this.paramsData[key];
        });
      } else {
        this.disabled = 'disabled' in this.options ? this.options.disabled : false;
      }
    },
    init() {
      // 缓存本次值用于和下次对比（由于上级传入的值每次都是新对象）
      this.nativeDef = this.defaultData;
      this.nativeDefPar = this.paramsData;

      this.nativeOptions = [];
      this.value = '';
      if (this.defaultData.options && this.nativeOptions.length === 0) {
        this.nativeOptions = this.defaultData.options.map(item => ({
          ...item,
          [this.options.label]: item[this.options.label].replace(/中国儿童中心\/+/, '')
        }));
        this.value = this.defaultData.value;
        // 初始化父级的value、label字段
        if (this.value === this.defaultData.options[0][this.options.value]) {
          this.$emit('input', this.value, this.defaultData.options[0][this.options.label], this.defaultData.options[0]);
        } else {
          this.$emit('input', this.value, this.nativeLabel, this.defaultData.options[0]);
        }
      }

      this.check();
    },
    remoteMethodType(value) {
      if (!value) {
        throw new Error(`请指定正确的远程搜索框请求方法key ${value}`);
      }
      const fetchMap = {
        org: base.searchOrg, // 部门搜索
        special: base.searchSpecial, // 子项目直接搜索
        specialRoot: base.specialRoot, // 项目大类搜索
        specialByRoot: base.getSpecialByOrg, // 子项目根据部门、项目大类级联搜索
        specialByChild: base.getSpecialChild, // 子项目根据项目大类级联搜索
        classRoom: classApi.classRoomQuery, // 教室
        building: base.queryBuilding, // 教学楼
        teacher: classApi.teacherQuery, // 教师
        assistant_teacher: classApi.teachingAssistantQuery, // 助教
        feeLevel: classApi.feeLevelQuery, // 级别
        contact: contact.contactQuery, // 学员
        class: classApi.classQuery, // 代课申请班级查询
        classQuery: classApi.classQueryByOrg, // 根据部门项目查询班级
        classSupplyTeacherQuery: classApi.classSupplyTeacherQuery, // 代课老师
        classIdQuery: classApi.classIdQuery,
        posts: cPosts.postsQuery,
        category: cPosts.categorySearch,
        newsCategory: cNews.newsCategorySearch,
        newsContent: cNews.getNewsContent
      };
      return fetchMap[value];
    },
    async remoteMethod(query) {
      if (this.disabled) return;
      this.loading = true;
      const res = await this.remoteMethodType(this.options.metholdKey)({
        query,
        ...this.paramsData,
        parent_id: this.paramsData.specialized_subject_id_parent
        // specialized_subject_id_parent: this.paramsData.specialized_subject_id_parent, // 项目大类ID
        // org_id: this.paramsData.org_id, // 部门ID
      });
      if (res && res.data) {
        if (this.options.staticResult) this.nativeOptions = [...this.options.staticResult, ...res.data.list];
        else this.nativeOptions = res.data.list;
        if (this.options.metholdKey === 'org') {
          // 过滤顶级机构 "中国儿童中心"
          this.nativeOptions = this.nativeOptions.filter(item => !item.is_top_org).map(item => ({
            ...item,
            [this.options.label]: item[this.options.label].replace(/中国儿童中心\/+/, '')
          }));
        }
      }
      this.loading = false;
    },
    async remoteMethodInput(query, cb) {
      this.loading = true;
      const res = await this.remoteMethodType(this.options.metholdKey)({
        query
      });
      if (res && res.data) {
        cb(res.data.list);
      }
      this.loading = false;
    },
    handleSelect(key, value, obj) {
      if (Array.isArray(value) || !value) {
        const labels = [];
        const items = [];
        this.nativeOptions.map(item => {
          if (value.includes(item[this.options.value])) {
            labels.push(item[this.options.label]);
            items.push(item);
          }
          return item;
        });
        this.$emit('input', value, labels, items);
      } else {
        let temp = obj;
        if (!temp) {
          temp = this.nativeOptions.filter(item => item[this.options.value] === value);
        }
        this.nativeLabel = temp[0][this.options.label];
        this.$emit('input', value, temp[0][this.options.label], temp[0]);
      }
    }
  }
};
</script>

<style lang="less">
.ly-search-comp {
  max-height: 300px;
  // overflow: auto;
  .el-input {
    width: 202px;
  }
}

</style>
